import { Button, ButtonProps, CircularProgress } from "@mui/material";
import { Save, Send } from "@mui/icons-material";
import { FormState } from "@/app/features/shared/form-builder/types/types";
import { ComponentRef, forwardRef, useMemo } from "react";

type ButtonLabels = { default: string, submitting: string, failed: string, success: string };

interface FormSubmitButtonProps {
    formState: FormState;
    onSubmit: () => void;
    purpose?: 'save' | 'send';
    buttonLabels?: Partial<ButtonLabels>
}

const FormSubmitButton = forwardRef<
    ComponentRef<typeof Button>,
    ButtonProps & FormSubmitButtonProps
>(
    ({ formState, buttonLabels, onSubmit, purpose = 'save', ...rest }, ref) => {

        const icon = useMemo(() => {
            if (formState.isSubmitting) {
                return <CircularProgress />
            }
            switch (purpose) {
                case 'save':
                    return <Save />
                case 'send':
                    return <Send />
            }
        }, [purpose]);

        const buttonText = useMemo(() => {

            let defaultButtonLabels: ButtonLabels;
            switch (purpose) {
                case 'save':
                    defaultButtonLabels = {
                        default: "Speichern",
                        submitting: "verarbeite ...",
                        failed: "Fehler",
                        success: "Gespeichert",
                    }
                    break;
                case 'send':
                    defaultButtonLabels = {
                        default: "Absenden",
                        submitting: "sende ...",
                        failed: "Fehler",
                        success: "Gesendet",
                    }
                    break;
            }

            const effectiveButtonLabels = { ...defaultButtonLabels, ...buttonLabels }

            if (formState.isSubmitting) {
                return effectiveButtonLabels.submitting;
            }

            if (formState.success == false && (formState.error || formState.fieldErrors)) {
                return effectiveButtonLabels.failed;
            }

            if (formState.success) {
                return effectiveButtonLabels.success;
            }

            return effectiveButtonLabels.default;

        }, [purpose, formState]);


        const color = useMemo(() => {
            if (formState.isSubmitting) {
                return "primary";
            }

            if (formState.success) {
                return "success";
            }

            if (formState.error) {
                return "error";
            }

            return "primary";
        }, [formState]);

        return (
            <Button
                ref={ref}
                variant={formState.isSubmitting ? "outlined" : "contained"}
                color={color}
                type="submit"
                startIcon={icon}
                disabled={!formState.isValid}
                onClick={(e) => {
                    e.preventDefault();
                    onSubmit();
                }}
                {...rest}
            >
                {buttonText}
            </Button>
        )
    });

FormSubmitButton.displayName = "FormSubmitButton";
export default FormSubmitButton;
